import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BadgeCmp from 'src/components/BadgeCmp';
import CheckBox from 'src/components/CheckBox';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { PATH } from 'src/constants/path';
import { getDateWithSuffixFormat, getFormattedDate } from 'src/utils/CommonFunctions';

import { INVOICE_COLUMN_CLASSNAME_MAP } from '../documentHub.const';
import { getLatestPodUploadedAt } from '../documentHub.helper';

import DaysPendingBadge from './DaysPendingBadge';

interface InvoiceRowProps {
    data: any;
    index?: number;
    onRowClick?: (data: any) => void;
    onPodClick?: (data: any) => void;
    headCells?: any[];
    selectedIds?: number[];
    onToggleSelect?: (id: number) => void;
}

const InvoiceRow: React.FC<InvoiceRowProps> = ({ data, onRowClick, onPodClick, headCells, selectedIds, onToggleSelect }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const orderIdRef = useRef<HTMLParagraphElement>(null);

    const getStatusBadge = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'paid':
                return (
                    <BadgeCmp style="modern" type="success">
                        {t('Common.Paid')}
                    </BadgeCmp>
                );
            case 'pending':
                return (
                    <BadgeCmp style="modern" type="warning">
                        {t('Common.Pending')}
                    </BadgeCmp>
                );
            case 'overdue':
                return (
                    <BadgeCmp style="modern" type="error">
                        {t('Common.Overdue')}
                    </BadgeCmp>
                );
            default:
                return (
                    <BadgeCmp style="modern" type="gray">
                        {status}
                    </BadgeCmp>
                );
        }
    };

    const dueDateLabel = useMemo(() => {
        // Client decision — once paid, the due date is moot; show when the customer actually paid
        // the full amount instead of a "Due <date>" tooltip that no longer means anything.
        if (data?.status?.toLowerCase() === 'paid') {
            if (!data?.paidAt) return null;

            return t('DocumentHub.Paid_On', { date: getFormattedDate(data.paidAt, 'MMMM Do, YYYY', false, false) });
        }

        if (!data?.dueDate) return null;

        const date = getFormattedDate(data.dueDate, 'MMMM Do, YYYY', false, false);

        // Overdue rows read "Overdue <date>" — the days-overdue count already shows on the badge
        // itself, so repeating it here would be redundant.
        if (typeof data?.daysPending === 'number' && data.daysPending < 0) {
            return t('DocumentHub.Due_On_Overdue', { date });
        }

        return t('DocumentHub.Due_On', { date });
    }, [data?.dueDate, data?.daysPending, data?.status, data?.paidAt, t]);

    const handleRowClick = () => {
        if (onRowClick) onRowClick(data);
    };

    // Order ID opens the order itself; falls back to the invoice summary when no order link is available
    const handleOrderIdClick = (e: React.MouseEvent) => {
        const orderLinkId = data?.walOrderId ?? data?.order?.walOrderId ?? null;

        if (orderLinkId) {
            e.stopPropagation();
            navigate(`${PATH.ORDER_VIEW}/${orderLinkId}`);
        } else {
            handleRowClick();
        }
    };

    // Newest upload across this row's visible PODs (BE value, FE fallback) — see helper.
    const podUploadedAt = getLatestPodUploadedAt(data);

    const tableCells = useMemo(
        () => [
            {
                key: 'invoiceNumber',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.invoiceNumber} cursor-pointer`} onClick={handleRowClick}>
                        {/* "View Invoice" link (same treatment as the POD cell) opens the invoice modal on its
                            Preview tab. The invoice number is kept discoverable via the hover tooltip. */}
                        {data?.invoiceNumber ? (
                            <TooltipCmp message={data.invoiceNumber} position="top">
                                <span className="text-utilityBlack800 font-semibold hover:text-primary cursor-pointer">{t('DocumentHub.View_Invoice')}</span>
                            </TooltipCmp>
                        ) : (
                            <span className="text-grayLight600 font-normal leading-[18px]">-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'orderId',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.orderId} cursor-pointer`} onClick={handleOrderIdClick}>
                        <TruncateTextCmp refName={orderIdRef} tooltipType="action" message={data?.orderId ? `#${data.orderId}` : '-'}>
                            <p className={`truncate ${data?.orderId ? 'text-utilityBlack800 font-semibold hover:text-primary' : ''}`} ref={orderIdRef}>
                                {data?.orderId ? `#${data.orderId}` : '-'}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'status',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.status} cursor-pointer`} onClick={handleRowClick}>
                        {data?.status ? <div className="flex">{getStatusBadge(data?.status)}</div> : <span className="text-grayLight600 font-normal leading-[18px]">-</span>}
                    </td>
                ),
            },
            {
                key: 'dueDate',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.dueDate} cursor-pointer`} onClick={handleRowClick}>
                        {/* Not every invoice has a due date on record (see computeInvoiceStatus in
                            getInvoicesList) — render a dash rather than the empty string
                            getFormattedDate returns for null, so the cell never looks broken. */}
                        {data?.dueDate ? (
                            <span
                                className="text-grayLight600 font-normal leading-[18px]"
                                dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(getFormattedDate(data.dueDate, 'MMMM Do, YYYY')) }}
                            />
                        ) : (
                            <span className="text-grayLight600 font-normal leading-[18px]">-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'dateInvoiced',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.dateInvoiced} cursor-pointer`} onClick={handleRowClick}>
                        {/* POD-only rows have no invoice date — getFormattedDate returns '' for null, render a dash instead */}
                        {data?.dateInvoiced ? (
                            <span
                                className="text-grayLight600 font-normal leading-[18px]"
                                dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(getFormattedDate(data.dateInvoiced, 'MMMM Do, YYYY')) }}
                            />
                        ) : (
                            <span className="text-grayLight600 font-normal leading-[18px]">-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'lastUpdated',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.lastUpdated} cursor-pointer`} onClick={handleRowClick}>
                        {data?.lastUpdated ? (
                            <span
                                className="text-grayLight600 font-normal leading-[18px]"
                                dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(getFormattedDate(data.lastUpdated, 'MMMM Do, YYYY')) }}
                            />
                        ) : (
                            <span className="text-grayLight600 font-normal leading-[18px]">-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'totalPrice',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.totalPrice} cursor-pointer`} onClick={handleRowClick}>
                        <span className="text-grayLight600 font-normal leading-[18px]">{data?.totalPrice || '-'}</span>
                    </td>
                ),
            },
            {
                key: 'paymentTerms',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.paymentTerms} cursor-pointer`} onClick={handleRowClick}>
                        <span className="text-grayLight600 font-normal leading-[18px]">{data?.paymentTerms || '-'}</span>
                    </td>
                ),
            },
            {
                key: 'daysPending',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.daysPending} cursor-pointer`} onClick={handleRowClick}>
                        {/* Hovering shows the actual due date behind the relative label, so "57 days
                            left" is always traceable to a real date without opening the invoice. */}
                        {dueDateLabel ? (
                            <TooltipCmp message={dueDateLabel} position="top">
                                <DaysPendingBadge daysPending={data?.daysPending} status={data?.status} />
                            </TooltipCmp>
                        ) : (
                            <DaysPendingBadge daysPending={data?.daysPending} status={data?.status} />
                        )}
                    </td>
                ),
            },
            {
                key: 'pod',
                htmlDynamic: (
                    <td className={INVOICE_COLUMN_CLASSNAME_MAP.pod}>
                        <div className="flex items-center h-full">
                            {data?.podDocuments?.length ? (
                                // Text link with the same hover treatment as the Order ID cell
                                <span
                                    className="text-utilityBlack800 font-semibold hover:text-primary cursor-pointer"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        onPodClick?.(data);
                                    }}
                                >
                                    {t('DocumentHub.View_Pod')}
                                </span>
                            ) : (
                                <span className="text-grayLight600 font-normal leading-[18px]">-</span>
                            )}
                        </div>
                    </td>
                ),
            },
            {
                key: 'podUploadedAt',
                htmlDynamic: (
                    <td className={`${INVOICE_COLUMN_CLASSNAME_MAP.podUploadedAt} cursor-pointer`} onClick={handleRowClick}>
                        {podUploadedAt ? (
                            <span
                                className="text-grayLight600 font-normal leading-[18px]"
                                dangerouslySetInnerHTML={{ __html: getDateWithSuffixFormat(getFormattedDate(podUploadedAt, 'MMMM Do, YYYY')) }}
                            />
                        ) : (
                            <span className="text-grayLight600 font-normal leading-[18px]">-</span>
                        )}
                    </td>
                ),
            },
        ],
        [data, t, podUploadedAt]
    );

    if (!headCells) {
        return (
            <>
                {tableCells.map((c) => (
                    <React.Fragment key={c.key}>{c.htmlDynamic}</React.Fragment>
                ))}
            </>
        );
    }

    return (
        <>
            {headCells
                .filter((cell: any) => cell.visible)
                .map((cell: any) => {
                    if (cell.id === 'checkbox') {
                        return (
                            <React.Fragment key="checkbox">
                                <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip" onClick={(e) => e.stopPropagation()}>
                                    {/* POD-only rows have no invoice id — bulk actions can't apply, so no checkbox */}
                                    {data?.id ? (
                                        <CheckBox
                                            id={`select-invoice-${data.id}`}
                                            checked={!!selectedIds?.includes(data.id)}
                                            onChangeFunc={(e: any) => {
                                                e.stopPropagation();
                                                onToggleSelect?.(data.id);
                                            }}
                                            name="selectInvoice"
                                            value={String(data.id)}
                                            parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                                        />
                                    ) : (
                                        // Keeps the sticky column's divider/shadow unbroken on rows with no checkbox
                                        <div className="table-col-tooltip-space-common-xs h-full items-center" />
                                    )}
                                </td>
                            </React.Fragment>
                        );
                    }

                    const matched = tableCells.find((tc) => tc.key === cell.id);
                    if (!matched) return null;

                    return <React.Fragment key={cell.id}>{matched.htmlDynamic}</React.Fragment>;
                })}
        </>
    );
};

export default InvoiceRow;
